import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import logger from '../../utils/logger';
import { resetAllData, syncAllDataToServer } from '../../services/syncService';
import { getAuthDataFromStorage } from '../../services/authService';
import storage from '../../utils/LocalStorageManager';
import { getRandomColor } from '../../utils/colorUtils';
import { get } from '../../services/apiClient';
import { getCurrentLanguage } from '../../config/languageConfig';

// 本地存储键名常量
const STORAGE_KEYS = {
  START_PAGE_PROMPT: 'hasPromptedStartPage',
};

// 获取启动页提示状态
export const fetchStartPagePromptStatus = createAsyncThunk(
  'settings/fetchStartPagePromptStatus',
  async (storageKey = STORAGE_KEYS.START_PAGE_PROMPT, { rejectWithValue }) => {
    try {
      const value = storage.getItem(storageKey);
      logger.log('从localStorage获取启动页状态:', storageKey, '=', value);
      return value;
    } catch (error) {
      logger.error('获取启动页提示状态失败:', error);
      return rejectWithValue('获取启动页提示状态失败');
    }
  }
);

// 重置所有数据的 thunk
export const resetAllDataThunk = createAsyncThunk(
  'settings/resetAllData',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await resetAllData(dispatch);
      if (!result.success) {
        logger.error('重置数据失败:',result.message);
      }
      return result;
    } catch (error) {
      logger.error('重置数据失败:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 导出数据
export const exportData = createAsyncThunk(
  'settings/exportData',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { navItems } = state.nav;
      const { websitesByNav } = state.website;
      
      // 将网站数据嵌套到对应的导航菜单中，与defaultNavItems格式保持一致
      const exportNavItems = navItems.map(navItem => ({
        ...navItem,
        websites: websitesByNav[navItem.id] || []
      }));
      
      const exportData = {
        navItems: exportNavItems,
        exportTime: new Date().toISOString(),
        version: '1.0.0'
      };
      
      // 将数据返回，由UI层处理下载逻辑
      return exportData;
    } catch (error) {
      logger.error('导出数据失败:', error);
      return rejectWithValue('导出数据失败');
    }
  }
);

// 导入数据
export const importData = createAsyncThunk(
  'settings/importData',
  async (importData, { dispatch, rejectWithValue }) => {
    try {
      if (!importData || !importData.navItems) {
        throw new Error('无效的数据格式');
      }
      
      // 分离导航菜单和网站数据
      const navItems = [];
      const websitesByNav = {};
      
      importData.navItems.forEach(navItem => {
        // 提取导航菜单项（不包含websites）
        const { websites, ...navItemWithoutWebsites } = navItem;
        navItems.push(navItemWithoutWebsites);
        
        // 提取网站数据
        if (websites && Array.isArray(websites)) {
          websitesByNav[navItem.id] = websites;
        }
      });
      
      // 更新导航菜单项
      dispatch({ type: 'nav/setNavItems', payload: navItems });
      
      // 更新所有网站数据
      dispatch({ 
        type: 'website/setAllWebsites', 
        payload: websitesByNav
      });
      
      // 触发更新事件
      window.dispatchEvent(new Event('navItemsUpdated'));
      window.dispatchEvent(new Event('websitesUpdated'));
      
      // 如果用户已登录，调用同步服务将数据同步到服务器
      const authData = getAuthDataFromStorage();
      if (authData && authData.token) {
        try {
          logger.log('用户已登录，开始同步导入的数据到服务器...');
          const syncResult = await syncAllDataToServer(dispatch);
          if (syncResult.success) {
            logger.log('导入数据同步到服务器成功');
          } else {
            logger.warn('导入数据同步到服务器失败:', syncResult.message);
          }
        } catch (error) {
          logger.error('导入数据同步到服务器时出错:', error);
        }
      }
      
      return true;
    } catch (error) {
      logger.error('导入数据失败:', error);
      return rejectWithValue('导入数据失败');
    }
  }
);

// 导入Chrome书签
export const importChromeBookmarks = createAsyncThunk(
  'settings/importChromeBookmarks',
  async (htmlContent, { dispatch, getState, rejectWithValue }) => {
    try {
      logger.log('开始导入Chrome书签文件');
      
      // 检查HTML内容是否有效
      if (!htmlContent || typeof htmlContent !== 'string') {
        throw new Error('无效的书签文件内容');
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      // 获取网站图标的函数
      const fetchWebsiteIcon = async (url) => {
        try {
          // 确保URL格式正确
          let processedUrl = url;
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            processedUrl = 'https://' + url;
          }
          
          // 使用API获取网站信息，设置2秒超时
          const apiUrl = `/proxy/website-info?url=${encodeURIComponent(processedUrl)}`;
          
          // 创建超时Promise
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('请求超时')), 2000);
          });
          
          // 使用Promise.race实现超时控制
          const response = await Promise.race([
            get(apiUrl),
            timeoutPromise
          ]);
          
          // 直接返回图标URL，不需要验证
          return response?.ico || null;
        } catch (error) {
          logger.error('获取网站图标失败:', error);
          return null;
        }
      };
      
      const generateId = () => 'chrome_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      const convertBookmarksToNavItems = async () => {
        const navItems = [];
        const websitesByNav = {};
        
        // 查找所有书签文件夹（H3标签）
        const folderElements = doc.querySelectorAll('DT > H3');
        logger.log(`找到 ${folderElements.length} 个书签文件夹`);
        
        // 使用 for...of 来支持异步操作
        for (const [index, h3Element] of folderElements.entries()) {
          const folderName = h3Element.textContent.trim();
          logger.log(`处理文件夹 ${index + 1}: "${folderName}"`);
          
          // 跳过空文件夹名
          if (!folderName) {
            logger.log('跳过空文件夹名');
            continue;
          }
          
          // 创建导航项
          const navId = `chrome_folder_${Date.now()}_${index}`;
          const navItem = {
            id: navId,
            text: folderName,
            icon: 'fas fa-folder',
            index: index,
            disableDrag: false,
            route: `/category/${navId}`
          };
          
          navItems.push(navItem);
          
          // 解析该文件夹下的书签
          const websites = [];
          const dtElement = h3Element.parentElement;
          
          // 查找下一个DL元素（包含书签链接）
          let nextElement = dtElement.nextElementSibling;
          while (nextElement && nextElement.nodeType !== 1) {
            nextElement = nextElement.nextElementSibling;
          }
          
          if (nextElement && nextElement.tagName === 'DL') {
            // 在DL元素中查找所有的 DT > A 链接
            const links = nextElement.querySelectorAll('DT > A');
            logger.log(`在DL中找到 ${links.length} 个链接`);
            
            // 使用 for...of 来支持异步操作
            for (const [linkIndex, link] of links.entries()) {
              const url = link.getAttribute('href');
              const title = link.textContent.trim();
              
              logger.log(`链接 ${linkIndex + 1}: "${title}" -> ${url}`);
              
              // 跳过无效链接
              if (!url || !title) {
                logger.log('跳过无效链接');
                continue;
              }
              
              // 尝试获取网站图标
              const iconUrl = await fetchWebsiteIcon(url);
              
              // 根据是否有图标设置背景色
              const bgColor = iconUrl ? 'white' : getRandomColor();
              
              // 创建网站项
              const website = {
                id: generateId(),
                name: title,
                url: url,
                iconColor: iconUrl ? '#333' : '#ffffff',
                iconUrl: iconUrl,
                textIcon: title.substring(0, 4), // 取前4个字符作为文字图标
                ico: iconUrl,
                description: `从Chrome书签导入：${title}`,
                bgColor: bgColor,
                preferredIconType: iconUrl ? '1' : '2'
              };
              
              websites.push(website);
              
              // 添加延迟，避免请求过于频繁
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          } else {
            logger.log('未找到对应的DL元素');
            // 尝试另一种方法：直接在父级DL中查找所有链接
            logger.log('尝试在父级DL中查找链接...');
            const parentDL = dtElement.parentElement;
            if (parentDL && parentDL.tagName === 'DL') {
              logger.log('找到父级DL元素');
              // 查找当前DT之后、下一个H3之前的所有A链接
              let currentElement = dtElement.nextElementSibling;
              while (currentElement) {
                if (currentElement.nodeType === 1) {
                  if (currentElement.tagName === 'DT' && currentElement.querySelector('H3')) {
                    // 遇到下一个文件夹，停止
                    break;
                  }
                  if (currentElement.tagName === 'DT') {
                    const link = currentElement.querySelector('A');
                    if (link) {
                      const url = link.getAttribute('href');
                      const title = link.textContent.trim();
                      
                      logger.log(`在父级DL中找到链接: "${title}" -> ${url}`);
                      
                      if (url && title) {
                        // 尝试获取网站图标
                        const iconUrl = await fetchWebsiteIcon(url);
                        
                        // 根据是否有图标设置背景色
                        const bgColor = iconUrl ? 'white' : getRandomColor();
                        
                        // 创建网站项
                        const website = {
                          id: generateId(),
                          name: title,
                          url: url,
                          iconColor: iconUrl ? '#333' : '#ffffff',
                          iconUrl: iconUrl,
                          textIcon: title.substring(0, 4),
                          ico: iconUrl,
                          description: `从Chrome书签导入：${title}`,
                          bgColor: bgColor,
                          preferredIconType: iconUrl ? '1' : '2'
                        };
                        
                        websites.push(website);
                        
                        // 添加延迟，避免请求过于频繁
                        await new Promise(resolve => setTimeout(resolve, 200));
                      }
                    }
                  }
                }
                currentElement = currentElement.nextElementSibling;
              }
            }
          }
          
          websitesByNav[navId] = websites;
          
          // 添加调试日志
          logger.log(`Chrome书签解析：文件夹 "${folderName}" 包含 ${websites.length} 个书签`);
        }
        
        logger.log(`解析完成：${navItems.length} 个文件夹，总共 ${Object.values(websitesByNav).reduce((total, websites) => total + websites.length, 0)} 个书签`);
        
        return { navItems, websitesByNav };
      };
      
      const { navItems, websitesByNav } = await convertBookmarksToNavItems();
      
      if (navItems.length === 0) {
        throw new Error('未找到有效的书签文件夹，请确保选择了正确的Chrome书签文件');
      }
      
      // 获取现有数据
      const state = getState();
      const existingNavItems = state.nav.navItems || [];
      const existingWebsites = state.website.websitesByNav || {};
      
      // 合并数据
      const mergedNavItems = [...existingNavItems, ...navItems];
      const mergedWebsites = { ...existingWebsites, ...websitesByNav };
      
      // 更新导航菜单项
      dispatch({ type: 'nav/setNavItems', payload: mergedNavItems });
      
      // 更新所有网站数据
      dispatch({ 
        type: 'website/setAllWebsites', 
        payload: mergedWebsites
      });
      
      // 触发更新事件
      window.dispatchEvent(new Event('navItemsUpdated'));
      window.dispatchEvent(new Event('websitesUpdated'));
      
      // 如果用户已登录，调用同步服务将数据同步到服务器
      const authData = getAuthDataFromStorage();
      if (authData && authData.token) {
        try {
          logger.log('用户已登录，开始同步Chrome书签导入的数据到服务器...');
          const syncResult = await syncAllDataToServer(dispatch);
          if (syncResult.success) {
            logger.log('Chrome书签导入数据同步到服务器成功');
          } else {
            logger.warn('Chrome书签导入数据同步到服务器失败:', syncResult.message);
          }
        } catch (error) {
          logger.error('Chrome书签导入数据同步到服务器时出错:', error);
        }
      }
      
      return {
        success: true,
        importedFolders: navItems.length,
        importedBookmarks: Object.values(websitesByNav).reduce((total, websites) => total + websites.length, 0)
      };
    } catch (error) {
      logger.error('导入Chrome书签失败:', error);
      return rejectWithValue(error.message || '导入Chrome书签失败');
    }
  }
);

const initialState = {
  startPagePromptStatus: null,
  resetStatus: 'idle',
  resetError: null,
  isResetting: false,
  isExporting: false,
  isImporting: false,
  resetSuccess: false,
  exportSuccess: false,
  importSuccess: false,
  error: null,
  autoFocusSearch: storage.getAutoFocusSearch(),
  showSearch: storage.getShowSearch(),
  showQuickSearch: storage.getShowQuickSearch(),
  dockVisible: storage.getItem('dockVisible') !== null ? storage.getItem('dockVisible') === 'true' : true,
  sideNavAutoHide: storage.getItem("sideNavAutoHide") !== null ? storage.getItem("sideNavAutoHide") === "true" : false,
  iconTextColor: storage.getItem('iconTextColor') || '#ffffff',
  iconSize: Math.min(Math.max(parseInt(storage.getItem('iconSize')) || 64, 32), 90),
  iconRadius: parseInt(storage.getItem('iconRadius')) || 12,
  iconSpacing: parseInt(storage.getItem('iconSpacing')) || 16,
  // 网站导航显示模式：'compact' - 紧凑显示，'detailed' - 详情显示
  websiteDisplayMode: storage.getItem('websiteDisplayMode') || 'compact',
  // 语言设置
  language: getCurrentLanguage(),
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setStartPagePromptStatus: (state, action) => {
      const { status, storageKey = STORAGE_KEYS.START_PAGE_PROMPT } = action.payload;
      state.startPagePromptStatus = status;
      try {
        storage.setItem(storageKey, status);
      } catch (error) {
        logger.error('保存启动页提示状态失败:', error);
      }
    },
    clearResetStatus: (state) => {
      state.resetSuccess = false;
      state.error = null;
    },
    clearExportStatus: (state) => {
      state.exportSuccess = false;
      state.error = null;
    },
    clearImportStatus: (state) => {
      state.importSuccess = false;
      state.error = null;
    },
    setAutoFocusSearch: (state, action) => {
      state.autoFocusSearch = action.payload;
      storage.setAutoFocusSearch(action.payload);
    },
    setShowSearch: (state, action) => {
      state.showSearch = action.payload;
      storage.setShowSearch(action.payload);
    },
    setShowQuickSearch: (state, action) => {
      state.showQuickSearch = action.payload;
      storage.setShowQuickSearch(action.payload);
    },
    setDockVisible: (state, action) => {
      state.dockVisible = action.payload;
      storage.setItem('dockVisible', action.payload.toString());
    },
    setSideNavAutoHide: (state, action) => {
      state.sideNavAutoHide = action.payload;
      storage.setItem('sideNavAutoHide', action.payload.toString());
    },
    setIconTextColor: (state, action) => {
      state.iconTextColor = action.payload;
      storage.setItem('iconTextColor', action.payload);
    },
    setIconSize: (state, action) => {
      // 确保图标大小在有效范围内（32-90px）
      const size = Math.min(Math.max(action.payload, 32), 90);
      state.iconSize = size;
      storage.setItem('iconSize', size.toString());
    },
    setIconRadius: (state, action) => {
      state.iconRadius = action.payload;
      storage.setItem('iconRadius', action.payload.toString());
    },
    setIconSpacing: (state, action) => {
      state.iconSpacing = action.payload;
      storage.setItem('iconSpacing', action.payload.toString());
    },
    setWebsiteDisplayMode: (state, action) => {
      state.websiteDisplayMode = action.payload;
      storage.setItem('websiteDisplayMode', action.payload);
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
      storage.setItem('language', action.payload);
      // 触发语言变更事件
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: action.payload } }));
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取启动页提示状态
      .addCase(fetchStartPagePromptStatus.fulfilled, (state, action) => {
        // 确保即使从localStorage中读取到null或undefined，也能正确设置状态
        state.startPagePromptStatus = action.payload || null;
        logger.log('Redux状态更新 startPagePromptStatus:', state.startPagePromptStatus);
      })
      
      // 重置数据
      .addCase(resetAllDataThunk.pending, (state) => {
        state.resetStatus = 'loading';
        state.resetError = null;
      })
      .addCase(resetAllDataThunk.fulfilled, (state) => {
        state.resetStatus = 'succeeded';
        state.resetError = null;
      })
      .addCase(resetAllDataThunk.rejected, (state, action) => {
        state.resetStatus = 'failed';
        state.resetError = action.payload;
      })
      
      // 导出数据
      .addCase(exportData.pending, (state) => {
        state.isExporting = true;
        state.exportSuccess = false;
        state.error = null;
      })
      .addCase(exportData.fulfilled, (state) => {
        state.isExporting = false;
        state.exportSuccess = true;
      })
      .addCase(exportData.rejected, (state, action) => {
        state.isExporting = false;
        state.error = action.payload;
      })
      
      // 导入数据
      .addCase(importData.pending, (state) => {
        state.isImporting = true;
        state.importSuccess = false;
        state.error = null;
      })
      .addCase(importData.fulfilled, (state) => {
        state.isImporting = false;
        state.importSuccess = true;
      })
      .addCase(importData.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload;
      })
      
      // 导入Chrome书签
      .addCase(importChromeBookmarks.pending, (state) => {
        state.isImporting = true;
        state.importSuccess = false;
        state.error = null;
      })
      .addCase(importChromeBookmarks.fulfilled, (state) => {
        state.isImporting = false;
        state.importSuccess = true;
      })
      .addCase(importChromeBookmarks.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setStartPagePromptStatus,
  clearResetStatus,
  clearExportStatus,
  clearImportStatus,
  setAutoFocusSearch,
  setShowSearch,
  setShowQuickSearch,
  setDockVisible,
  setSideNavAutoHide,
  setIconTextColor,
  setIconSize,
  setIconRadius,
  setIconSpacing,
  setWebsiteDisplayMode,
  setLanguage
} = settingsSlice.actions;

export default settingsSlice.reducer; 